import { inject, Injectable } from '@angular/core';
import { doc, DocumentData, Firestore, getDoc, writeBatch } from '@angular/fire/firestore';
import { MeasurementEntry } from '../models/survey.model';
import {
  assertSupportedVersion,
  assertWithinLimit,
  estimateDocBytes,
  isSplitSurvey,
  joinSurvey,
  LocationPageDoc,
  locationPageDocId,
  MAX_INLINE_DOC_BYTES,
  SheetManifest,
  sheetsEqual,
  splitSurvey,
  SURVEY_MEASUREMENTS_SUBCOLLECTION,
  SurveyLayoutVersion,
} from '../models/survey-storage';

const MAX_BATCH_OPS = 400;
const MAX_BATCH_BYTES = 8 * 1024 * 1024;

const OWNER_FIELD = 'ownerId';

export type SurveyDocData = DocumentData & { measurements?: Record<string | number, MeasurementEntry> | null };

export interface LoadedSurveyDoc {
  data: SurveyDocData;
  isSplit: boolean;
  version?: number;
}

export type OnlineState = { kind: 'missing' } | { kind: 'unreadable' } | { kind: 'found'; doc: LoadedSurveyDoc };

export type SurveyWriteOp = { type: 'set'; path: string; data: DocumentData } | { type: 'delete'; path: string };

/**
 * Single place that knows how a survey is laid out in Firestore.
 *
 * A survey keeps the layout it was first saved with, so any app version that could read it before still can:
 *  - never saved online      -> version 2, or 3 when a sheet needs more than one page of locations
 *  - online as version 2     -> version 2, or 3 once a sheet needs a second page
 *  - online as version 3     -> stays version 3
 *  - online as version 1     -> stays version 1 (one document) while it fits in 1 MiB, otherwise split
 *  - online copy unreadable  -> version 1 while it fits in 1 MiB, otherwise split: if it is really version 2 the security rules reject the write,
 *                               so a guess can never downgrade or blank a survey
 */
@Injectable({ providedIn: 'root' })
export class SurveyStorageService {
  private readonly firestore = inject(Firestore);
  async load(surveyPath: string): Promise<LoadedSurveyDoc | null> {
    const metadata = await this.readDoc(surveyPath);
    if (!metadata) {
      return null;
    }
    assertSupportedVersion(metadata);
    if (!isSplitSurvey(metadata)) {
      return { data: metadata, isSplit: false };
    }

    const manifest = (metadata['sheets'] ?? {}) as SheetManifest;
    const createdBy = (metadata['audit'] as { createdBy?: string } | undefined)?.createdBy;
    // A sheet or page owned by someone other than the survey's creator was not written by the survey's owner: treat it as missing.
    const readOwned = async (path: string): Promise<DocumentData | undefined> => {
      const found = await this.readDoc(path);
      const foreign = found && createdBy && found[OWNER_FIELD] !== undefined && found[OWNER_FIELD] !== createdBy;
      return found && !foreign ? found : undefined;
    };
    const sheetEntries = Object.keys(manifest).map(async (index) => {
      const sheet = await readOwned(this.sheetPath(surveyPath, index));
      return [index, sheet ? this.stripOwner(sheet) : undefined] as const;
    });
    const pageEntries = Object.entries(manifest).flatMap(([index, summary]) =>
      Array.from({ length: Math.max(0, (summary.locationPages ?? 1) - 1) }, async (_, n): Promise<readonly [string, DocumentData | undefined]> => {
        const docId = locationPageDocId(index, n + 1);
        return [docId, await readOwned(this.sheetPath(surveyPath, docId))] as const;
      }),
    );
    const [sheetResults, pageResults] = await Promise.all([Promise.all(sheetEntries), Promise.all(pageEntries)]);
    const sheets = Object.fromEntries(sheetResults.filter(([, sheet]) => sheet !== undefined)) as Record<string, MeasurementEntry>;
    const pages = Object.fromEntries(pageResults.filter(([, page]) => page !== undefined)) as Record<string, LocationPageDoc>;
    return { data: joinSurvey(metadata, sheets, pages) as SurveyDocData, isSplit: true, version: metadata['version'] as number };
  }

  async save(surveyPath: string, survey: SurveyDocData, online: OnlineState): Promise<SurveyLayoutVersion> {
    switch (online.kind) {
      case 'missing':
        return this.saveSplit(surveyPath, survey, null);
      case 'unreadable':
        return this.saveInline(surveyPath, survey);
      case 'found':
        return online.doc.isSplit ? this.saveSplit(surveyPath, survey, online.doc) : this.saveInline(surveyPath, survey);
    }
  }

  private async saveSplit(surveyPath: string, survey: SurveyDocData, previous: LoadedSurveyDoc | null): Promise<SurveyLayoutVersion> {
    const split = splitSurvey(survey, previous?.version);
    assertWithinLimit(split, surveyPath);

    const ownerId = (survey['audit'] as { createdBy?: string } | undefined)?.createdBy;
    const previousSplit = previous ? splitSurvey(previous.data, split.metadata.version) : null;
    const withOwner = (data: DocumentData): DocumentData => ({ ...data, ...(ownerId && { [OWNER_FIELD]: ownerId }) });

    const sheetOps: SurveyWriteOp[] = [
      ...Object.entries(split.sheets)
        .filter(([index, sheet]) => !previousSplit || !sheetsEqual(previousSplit.sheets[index], sheet))
        .map(([index, sheet]): SurveyWriteOp => ({ type: 'set', path: this.sheetPath(surveyPath, index), data: withOwner(sheet) })),
      ...Object.entries(split.pages)
        .filter(([docId, page]) => !previousSplit || !sheetsEqual(previousSplit.pages[docId], page))
        .map(([docId, page]): SurveyWriteOp => ({ type: 'set', path: this.sheetPath(surveyPath, docId), data: withOwner(page) })),
    ];
    const deleteOps: SurveyWriteOp[] = [
      ...Object.keys(previousSplit?.sheets ?? {}).filter((index) => !(index in split.sheets)),
      ...Object.keys(previousSplit?.pages ?? {}).filter((docId) => !(docId in split.pages)),
    ].map((id): SurveyWriteOp => ({ type: 'delete', path: this.sheetPath(surveyPath, id) }));
    const metadataOp: SurveyWriteOp = { type: 'set', path: surveyPath, data: split.metadata };

    await this.commit(this.planBatches(sheetOps, deleteOps, metadataOp));
    return split.metadata.version;
  }

  private async saveInline(surveyPath: string, survey: SurveyDocData): Promise<SurveyLayoutVersion> {
    const bytes = estimateDocBytes(survey);
    if (bytes > MAX_INLINE_DOC_BYTES) {
      // Too big for one document: move it to the split layout rather than failing the publish.
      return this.saveSplit(surveyPath, survey, null);
    }
    await this.commit([[{ type: 'set', path: surveyPath, data: survey }]]);
    return 1;
  }

  async deleteAll(surveyPath: string): Promise<void> {
    const metadata = await this.readDoc(surveyPath).catch((): null => null);
    const manifest = metadata && isSplitSurvey(metadata) ? ((metadata['sheets'] ?? {}) as SheetManifest) : {};
    const ids = Object.entries(manifest).flatMap(([index, summary]) => [
      index,
      ...Array.from({ length: Math.max(0, (summary.locationPages ?? 1) - 1) }, (_, n): string => locationPageDocId(index, n + 1)),
    ]);
    const ops: SurveyWriteOp[] = [{ type: 'delete', path: surveyPath }, ...ids.map((id): SurveyWriteOp => ({ type: 'delete', path: this.sheetPath(surveyPath, id) }))];
    await this.commit(this.chunk(ops));
  }

  private planBatches(sheetOps: SurveyWriteOp[], deleteOps: SurveyWriteOp[], metadataOp: SurveyWriteOp): SurveyWriteOp[][] {
    const all = [...sheetOps, ...deleteOps, metadataOp];
    const single = this.chunk(all);
    if (single.length === 1) {
      return single;
    }
    return [...this.chunk(sheetOps), [metadataOp], ...this.chunk(deleteOps)].filter((batch) => batch.length > 0);
  }

  private chunk(ops: SurveyWriteOp[]): SurveyWriteOp[][] {
    const batches: SurveyWriteOp[][] = [];
    let current: SurveyWriteOp[] = [];
    let bytes = 0;
    for (const op of ops) {
      const opBytes = op.type === 'set' ? estimateDocBytes(op.data) : 0;
      if (current.length >= MAX_BATCH_OPS || (current.length > 0 && bytes + opBytes > MAX_BATCH_BYTES)) {
        batches.push(current);
        current = [];
        bytes = 0;
      }
      current.push(op);
      bytes += opBytes;
    }
    if (current.length > 0) {
      batches.push(current);
    }
    return batches;
  }

  private sheetPath(surveyPath: string, index: string): string {
    return `${surveyPath}/${SURVEY_MEASUREMENTS_SUBCOLLECTION}/${index}`;
  }

  private stripOwner(sheet: DocumentData): MeasurementEntry {
    const { [OWNER_FIELD]: _owner, ...rest } = sheet;
    return rest as MeasurementEntry;
  }

  protected async readDoc(path: string): Promise<DocumentData | null> {
    const snapshot = await getDoc(doc(this.firestore, path));
    return snapshot.exists() ? snapshot.data() : null;
  }

  protected async commit(batches: SurveyWriteOp[][]): Promise<void> {
    for (const ops of batches) {
      const batch = writeBatch(this.firestore);
      for (const op of ops) {
        const ref = doc(this.firestore, op.path);
        if (op.type === 'set') {
          batch.set(ref, op.data);
        } else {
          batch.delete(ref);
        }
      }
      await batch.commit();
    }
  }
}
